// src/components/CreatePranayamaDialog.jsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  InputAdornment,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Delete as DeleteIcon,
  AccessTime as AccessTimeIcon,
  VolumeUpOutlined,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createPranayamma } from 'services/pranayamma/createPranayamma';
import { useParams } from 'react-router-dom';

const PHASE_TYPES = [
  { value: 'inhale', label: 'Inhale' },
  { value: 'hold', label: 'Hold' },
  { value: 'exhale', label: 'Exhale' },
];

const CreatePranayamaDialog = ({ open, onClose, onSave }) => {
  const queryClient = useQueryClient();
  const { courseid } = useParams();

  const [form, setForm] = useState({
    name: '',
    description: '',
    loops: '10',
    phases: [
      { type: 'inhale', seconds: '4' },
      { type: 'hold', seconds: '4' },
      { type: 'exhale', seconds: '4' },
    ],
    steps: '',
    benefits: '',
    difficultyLevel: 'Beginner',
  });

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      loops: '10',
      phases: [
        { type: 'inhale', seconds: '4' },
        { type: 'hold', seconds: '4' },
        { type: 'exhale', seconds: '4' },
      ],
      steps: '',
      benefits: '',
      difficultyLevel: 'Beginner',
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Check if all phases have a selected type
  const allPhasesHaveType = form.phases.every((p) => p.type?.trim() !== '');

  const addPhase = () => {
    if (form.phases.length >= 12) {
      toast.error('Maximum 12 phases allowed');
      return;
    }

    if (!allPhasesHaveType) {
      toast.error('Please select a type for all existing phases before adding a new one');
      return;
    }

    // Add new phase with empty type
    setForm((prev) => ({
      ...prev,
      phases: [...prev.phases, { type: '', seconds: '4' }],
    }));
  };

  const updatePhase = (index, field, value) => {
    setForm((prev) => {
      const newPhases = [...prev.phases];

      // Prevent consecutive same types
      if (field === 'type') {
        const prevPhase = index > 0 ? newPhases[index - 1] : null;
        if (prevPhase && prevPhase.type === value) {
          toast.error('Consecutive phases cannot be the same type');
          return prev;
        }
      }

      newPhases[index] = { ...newPhases[index], [field]: value };
      return { ...prev, phases: newPhases };
    });
  };

  const removePhase = (index) => {
    if (form.phases.length <= 1) {
      toast.error('At least one phase is required');
      return;
    }
    setForm((prev) => ({
      ...prev,
      phases: prev.phases.filter((_, i) => i !== index),
    }));
  };

  const movePhase = (index, direction) => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === form.phases.length - 1)
    )
      return;

    const newPhases = [...form.phases];
    const target = direction === 'up' ? index - 1 : index + 1;
    [newPhases[index], newPhases[target]] = [newPhases[target], newPhases[index]];
    setForm((prev) => ({ ...prev, phases: newPhases }));
  };

  const createPranayammaMutation = useMutation({
    mutationFn: createPranayamma,
    onSuccess: (data) => {
      console.log("👌",data)
      toast.success(data?.message || 'Pranayama created successfully!');
      queryClient.invalidateQueries(['pranayammas']);
      resetForm();
      onClose();
      onSave?.();
    },
    onError: (error) => {
    console.log("👌",error)
    toast.error(error?.response?.data?.message || 'Failed to create pranayama');
    },
  });

  const handleSubmit = () => {
    if (!form.name?.trim()) {
      toast.error('Pranayama name is required');
      return;
    }

  


    if (!allPhasesHaveType) {
      toast.error('All phases must have a selected type');
      return;
    }

    const totalSeconds = form.phases.reduce((sum, p) => sum + Number(p.seconds || 0), 0);
    if (totalSeconds === 0) {
      toast.error('At least one phase must have duration > 0');
      return;
    }

    if (Number(form.loops) < 1) {
      toast.error('Number of rounds must be at least 1');
      return;
    }

    // Check consecutive same types
    for (let i = 1; i < form.phases.length; i++) {
      if (form.phases[i].type === form.phases[i - 1].type) {
        toast.error('Consecutive phases cannot have the same type');
        return;
      }
    }

    const payload = {
      courseId: Number(courseid),
      name: form.name.trim(),
      duration: totalSeconds,
      loops: Number(form.loops),
      difficultyLevel: form.difficultyLevel,
      breathingSequence: form.phases.map((p) => ({
        type: p.type,
        seconds: Number(p.seconds) || 0,
      })),
      instructions: form.steps.trim(),
      benefits: form.benefits
        ? form.benefits.split('\n').map((line) => line.trim()).filter(Boolean)
        : [],
      description: form.description.trim(),
    };

    createPranayammaMutation.mutate(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            Create New Pranayama
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3} mt={1}>
          {/* Name */}
          <TextField
            autoFocus
            label="Pranayama Name"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="e.g. Anulom Vilom, 4-7-8 Breathing"
            fullWidth
            required
          />

          {/* Rounds & Difficulty */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Number of Rounds"
              name="loops"
              value={form.loops}
              onChange={handleChange}
              type="number"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccessTimeIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              inputProps={{ min: 1 }}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Difficulty Level</InputLabel>
              <Select
                name="difficultyLevel"
                value={form.difficultyLevel}
                label="Difficulty Level"
                onChange={handleChange}
              >
                <MenuItem value="Beginner">Beginner</MenuItem>
                <MenuItem value="Intermediate">Intermediate</MenuItem>
                <MenuItem value="Advanced">Advanced</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {/* Breathing Sequence */}
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1" fontWeight={600}>
                Breathing Sequence
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  (max 12 phases)
                </Typography>
              </Typography>

              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={addPhase}
                variant="outlined"
                disabled={form.phases.length >= 12 || !allPhasesHaveType}
              >
                Add Phase
              </Button>
            </Box>

            <Paper variant="outlined" sx={{ p: 1, bgcolor: 'grey.50' }}>
              <List disablePadding>
                {form.phases.map((phase, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <Divider />}
                    <ListItem sx={{ py: 1.5 }}>
                      {/* Phase Type Selector */}
                      <FormControl sx={{ minWidth: 140, mr: 2 }}>
                        {/* <InputLabel>Type</InputLabel> */}
                        <Select
                          value={phase.type}
                          onChange={(e) => updatePhase(index, 'type', e.target.value)}
                          size="small"
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected) {
                              return <em style={{ color: '#757575' }}>Select type</em>;
                            }
                            const found = PHASE_TYPES.find((opt) => opt.value === selected);
                            return found ? found.label : selected;
                          }}
                        >
                          {PHASE_TYPES.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Duration */}
                      <TextField
                        size="small"
                        type="number"
                        label="Seconds"
                        value={phase.seconds}
                        onChange={(e) => updatePhase(index, 'seconds', e.target.value)}
                        inputProps={{ min: 1 }}
                        sx={{ width: 110, mr: 2 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">s</InputAdornment>,
                        }}
                      />

                      {/* Controls */}
                      <ListItemSecondaryAction>
                        <IconButton
                          size="small"
                          onClick={() => movePhase(index, 'up')}
                          disabled={index === 0}
                        >
                          <ArrowUpIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => movePhase(index, 'down')}
                          disabled={index === form.phases.length - 1}
                        >
                          <ArrowDownIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removePhase(index)}
                          disabled={form.phases.length <= 1}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Paper>

            {/* Total duration preview */}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, textAlign: 'right' }}>
              Total per round:{' '}
              <strong>
                {form.phases.reduce((sum, p) => sum + Number(p.seconds || 0), 0)} seconds
              </strong>
            </Typography>
          </Box>

          {/* Instructions */}
        <FormControl fullWidth>
  <InputLabel shrink>
    <Box display="flex" alignItems="center" gap={0.5}>
      Steps / Instructions (for speech)
      <VolumeUpOutlined fontSize="small" color="action" />
    </Box>
  </InputLabel>

  <TextField
    name="steps"
    value={form.steps}
    onChange={(e) => {
      if (e.target.value.length <= 200) {
        handleChange(e);
      }
    }}
    multiline
    rows={4}
    fullWidth
    inputProps={{ maxLength: 200 }}
    helperText={`${form.steps.length}/200 characters`}
    placeholder="Sit comfortably, inhale slowly through the nose, hold gently, then exhale calmly..."
  />
</FormControl>



          {/* Benefits */}
          <TextField
            label="Benefits (one per line)"
            name="benefits"
            value={form.benefits}
            onChange={handleChange}
            multiline
            rows={3}
            fullWidth
            placeholder="• Reduces stress\n• Improves focus\n• Balances nervous system"
          />

          {/* Description */}
          <TextField
            label="Short Description (optional)"
            name="description"
            value={form.description}
            onChange={handleChange}
            multiline
            rows={2}
            fullWidth
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          size="large"
          disabled={createPranayammaMutation.isLoading || !allPhasesHaveType}
        >
          {createPranayammaMutation.isLoading ? 'Creating...' : 'Create Pranayama'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePranayamaDialog;